import React from "react";

import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

import PaperFragment from "../../../../helper-components/misc/PaperFragment";
import StackedHeader from "../../../../helper-components/misc/StackedHeader";
import DataGridTable from "../../../../helper-components/table/DataGridTable";

import { reviewColumns, defaultReviewTableOptions } from "../../../../helper-functions/table/highlights";
import { deleteHighlightReviews, queryHighlight, updateHighlightReview } from "../../../../helper-functions/api/highlightQueries";
import { useDataGridPaginationState } from "../../../../helper-functions/hooks/useDataGridPaginationState";
import { createUploadObject } from "../../../../helper-functions/shared/miscUtils";

import HighlightReviewEditForm from "./HighlightReviewEditForm";

import { getLanguage } from "../../../../config/language/language";
import { HIGHLIGHT_REVIEW_RESPONSE_OBJECT } from "../../../../config/constants/Constants";

/**
 * Table showing highlight requests.
 * If no data found, show error message instead.
 */
export default function HighlightReviewsTable(props) {
    const highlight = props.highlight;

    // Temporary state for highlight reviews (pagination variables required for the table)
    const highlightReviews = useDataGridPaginationState("highlight reviews", { initialSorting: { field: "created_on", sort: "desc" } });

    console.warn("highlightReviews", highlightReviews);

    React.useEffect(() => {
        if (!highlight) return;

        const data = JSON.parse(JSON.stringify(highlight?.highlight_reviews || []));
        const comment = JSON.parse(JSON.stringify(highlight?.meta_data?.comment || null));

        if (comment) {
            const systemReview = JSON.parse(JSON.stringify(HIGHLIGHT_REVIEW_RESPONSE_OBJECT));
            systemReview.note = comment ?? "N/A";
            systemReview.created_on = highlight?.created_on;
            data.unshift(systemReview);
        }

        highlightReviews.setData(data);
    }, [highlight]);

    const showDeleteDialog = () => {
        props.showDialog({
            title: "Delete highlight review(s)",
            message: `Are you sure you want to delete the ${highlightReviews.selected.length} selected highlight review(s)?`,
            actionTitle: "Delete",
            actionColor: "error",
            callback: (confirm) => handleDelete(confirm)
        });
    };

    const showUpdateDialog = () => {
        props.showDialog({
            title: "Edit author",
            message: "Click 'Update' to confirm changes.",
            component: <HighlightReviewEditForm {...props} row={highlightReviews.selected?.[0]} callback={(confirm, options) => handleUpdate(confirm, options)} />
        });
    };

    const handleDelete = (confirm) => {
        if (!confirm) return;

        const ids = highlightReviews.selected.map((request) => request.id);
        deleteHighlightReviews(props, { body: { ids }, ids, states: highlightReviews });
    };

    const handleUpdate = (confirm, options = {}) => {
        props.setOpenDialog(false); // Manually close the dialog.
        if (!confirm) return;

        const { row } = options;
        const body = createUploadObject(row, ["form"]);

        props.showSnackbar({ color: "info", message: getLanguage().SNACKBAR_INFO_LOADING });
        updateHighlightReview(props, { body, id: row.id, states: highlightReviews });
    };

    return (
        <PaperFragment
            title={
                <StackedHeader
                    {...props}
                    title="Reviews and Comments"
                    subtitle="View and manage existing highlight reviews"
                    icons={[
                        {
                            icon: <DeleteIcon />,
                            tooltip: "Delete selected highlight review(s)",
                            disabled: highlightReviews.selected.length === 0 || highlight?.status !== "AWAITING_APPROVAL",
                            onClick: showDeleteDialog
                        },
                        {
                            icon: <EditIcon />,
                            tooltip: "Edit selected highlight review",
                            disabled: highlightReviews.selected.length !== 1 || highlight?.status !== "AWAITING_APPROVAL",
                            onClick: showUpdateDialog
                        }
                    ]}
                />
            }
            value={
                <DataGridTable
                    {...props}
                    {...defaultReviewTableOptions}
                    rowHeight={100}
                    states={highlightReviews}
                    columns={reviewColumns(props)}
                    query={(options) => queryHighlight(props, { id: highlight.id })}
                    empty={getLanguage().NO_HIGHLIGHT_FOUND}
                    pagination={false}
                    refresh={15}
                />
            }
        />
    );
}
